import { useUser } from "@clerk/clerk-react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AlertCircle,
  Check,
  ExternalLink,
  FileText,
  Loader2,
  Lock,
  MoreVertical,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AutoAssignModal } from "@/components/admin/AutoAssignModal";
import { Autocomplete } from "@/components/admin/Autocomplete";
import { DataTable } from "@/components/admin/DataTable";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { useAdminScope } from "@/contexts/AdminScopeContext";
import {
  useAssignmentTopics,
  useAssignTopicToUser,
  useDeleteSubmission,
  useUpdateUserAssignment,
  useUserAssignments,
} from "@/hooks/admin/assignments";
import { useProgramme } from "@/hooks/admin/programmes";
import { AdminLayout } from "@/layouts/AdminLayout";
import { isSuperadminRole } from "@/lib/roles";
import { api } from "@/services/api";
import type { AssignmentMappingResponse, Topic, UserAssignmentWithUser } from "@/types";

interface TopicAssignModalProps {
  open: boolean;
  userAssignment: UserAssignmentWithUser;
  topics: Topic[];
  onClose: () => void;
}

function TopicAssignModal({ open, userAssignment, topics, onClose }: TopicAssignModalProps) {
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(userAssignment.topic_id);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { mutateAsync: updateUserAssignment } = useUpdateUserAssignment();
  const { mutateAsync: assignTopicToUser } = useAssignTopicToUser();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredTopics = topics.filter((t) => {
    return (
      t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const handleAssign = async () => {
    if (selectedTopicId === null) return;

    setIsSubmitting(true);
    setError(null);

    try {
      if (userAssignment.id) {
        // Update existing UserAssignment
        await updateUserAssignment({
          id: userAssignment.id,
          data: { topic_id: selectedTopicId },
        });
      } else {
        // Create UserAssignment + assign topic in one call
        await assignTopicToUser({
          userProgrammeId: userAssignment.user_programme_id,
          assignmentId: userAssignment.assignment_id,
          topicId: selectedTopicId,
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to assign topic");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Assign Topic</h2>
            <p className="text-sm text-gray-500 mt-1">User: {userAssignment.username}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {userAssignment.topic_id && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                Current topic: <strong>{userAssignment.topic_title}</strong>
                {userAssignment.topic_genre && (
                  <span className="text-blue-500"> ({userAssignment.topic_genre})</span>
                )}
              </p>
            </div>
          )}

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
            <Input
              type="text"
              placeholder="Search topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Topics List */}
          <div className="max-h-[300px] overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-200">
            {filteredTopics.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No topics found</div>
            ) : (
              filteredTopics.map((topic) => (
                <button
                  key={topic.id}
                  onClick={() => setSelectedTopicId(topic.id)}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-start gap-3 ${
                    selectedTopicId === topic.id ? "bg-green-50" : ""
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      selectedTopicId === topic.id ? "border-primary bg-primary" : "border-gray-300"
                    }`}
                  >
                    {selectedTopicId === topic.id && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900">{topic.title}</div>
                    <div className="text-sm text-gray-500 truncate">{topic.description}</div>
                    {topic.tags
                      .filter((t) => t.tag_group === "topic_area")
                      .map((t) => (
                        <span
                          key={t.id}
                          className="inline-flex mt-1 mr-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full"
                        >
                          {t.display_name}
                        </span>
                      ))}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={isSubmitting || selectedTopicId === null}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 disabled:bg-primary/40 text-white rounded-lg transition-colors"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Assign Topic
          </button>
        </div>
      </div>
    </div>
  );
}

/** A user-assignment row flattened out of its mapping, carrying the window's
 * week/assignment so a single table can show rows across multiple windows. */
type FlatAssignmentRow = UserAssignmentWithUser & {
  _week: number;
  _assignmentNumber: number;
};

export default function AdminAssignmentsPage() {
  // Page-local filter: user autocomplete. Cohort + W#A# come from the shared
  // admin scope (topbar switcher).
  const [userFilter, setUserFilter] = useState<string | null>(null);

  // Row actions / modals
  const [assigningUser, setAssigningUser] = useState<UserAssignmentWithUser | null>(null);
  const [autoAssignTarget, setAutoAssignTarget] = useState<AssignmentMappingResponse | null>(null);

  const topicsQuery = useAssignmentTopics();
  const topics = topicsQuery.data ?? [];

  // This endpoint requires a concrete programme, so "All cohorts" falls back
  // to the scope's default (first active cohort).
  const { programmeId, week, assignment, defaultProgrammeId, isLoadingProgrammes } =
    useAdminScope();
  const effectiveProgramme = programmeId ?? defaultProgrammeId ?? "";

  // Lock state (KC-287): the scope's programme shape lacks locked_at, so read the
  // detail. When locked, topic-assignment controls are frozen for admins but stay
  // active for superadmin (who can still edit a sealed cohort).
  const { user } = useUser();
  const isSuperadmin = isSuperadminRole(user?.publicMetadata?.role as string | undefined);
  const programmeDetailQuery = useProgramme(effectiveProgramme || undefined);
  const cohortLocked = Boolean(programmeDetailQuery.data?.locked_at);
  const controlsLocked = cohortLocked && !isSuperadmin;

  const mappingsQuery = useUserAssignments(
    effectiveProgramme
      ? {
          programmeId: effectiveProgramme,
          weekNumber: week ?? undefined,
          assignmentNumber: assignment ?? undefined,
        }
      : null
  );
  const mappings = useMemo(() => mappingsQuery.data ?? [], [mappingsQuery.data]);
  const mappingsError = mappingsQuery.isError
    ? mappingsQuery.error instanceof Error
      ? mappingsQuery.error.message
      : "Failed to load assignments"
    : null;

  const { mutateAsync: deleteSubmission } = useDeleteSubmission();

  const handleDeleteSubmission = useCallback(
    async (submissionId: string) => {
      if (
        !confirm(
          "Are you sure you want to remove this submission? This will also delete the review."
        )
      ) {
        return;
      }
      try {
        await deleteSubmission(submissionId);
      } catch (error) {
        console.error("Failed to delete submission:", error);
        alert("Failed to delete submission");
      }
    },
    [deleteSubmission]
  );

  // Flatten every window's user rows into one list, tagging each with its window.
  const rows: FlatAssignmentRow[] = mappings.flatMap((m) =>
    m.user_assignments.map((ua) => ({
      ...ua,
      _week: m.assignment.week_number,
      _assignmentNumber: m.assignment.assignment_number,
    }))
  );

  // Unique participants in view, for the user-filter autocomplete.
  const userOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const m of mappings) {
      for (const ua of m.user_assignments) {
        if (!byId.has(ua.user_id)) byId.set(ua.user_id, ua.username);
      }
    }
    return Array.from(byId, ([value, label]) => ({ value, label })).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [mappings]);

  const visibleRows = userFilter ? rows.filter((r) => r.user_id === userFilter) : rows;

  // Auto-assign is offered when exactly one window is in view and still needs topics.
  const autoAssignWindow =
    mappings.length === 1 && mappings[0].topics_assigned < mappings[0].total_enrolled
      ? mappings[0]
      : null;

  const columns = useMemo<ColumnDef<FlatAssignmentRow>[]>(
    () => [
      {
        id: "week",
        header: "Week",
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-gray-600">Week {row.original._week}</span>
        ),
      },
      {
        id: "assignment",
        header: "Assignment",
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-gray-600">#{row.original._assignmentNumber}</span>
        ),
      },
      {
        accessorKey: "username",
        header: "User",
        cell: ({ row }) => (
          <Link
            to={`/admin/users/${row.original.user_id}`}
            className="font-medium text-gray-900 whitespace-nowrap transition-colors hover:text-green-600"
          >
            {row.original.username}
          </Link>
        ),
      },
      {
        accessorKey: "topic_title",
        header: "Topic",
        cell: ({ row }) => {
          const ua = row.original;
          return ua.topic_title ? (
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-gray-900 truncate block max-w-xs">{ua.topic_title}</span>
              {ua.topic_genre && (
                <span className="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                  {ua.topic_genre}
                </span>
              )}
            </div>
          ) : (
            <span className="text-gray-400 italic">Not assigned</span>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => {
          const normalizedStatus = row.original.status.toLowerCase();
          const statusConfig: Record<string, { label: string; className: string }> = {
            not_started: { label: "Pending", className: "bg-gray-400 text-white" },
            in_progress: { label: "In Progress", className: "bg-blue-500 text-white" },
            submitted: { label: "Submitted", className: "bg-teal-500 text-white" },
            reviewed: { label: "Reviewed", className: "bg-green-500 text-white" },
            resubmitted: { label: "Resubmitted", className: "bg-teal-600 text-white" },
            final_review: { label: "Final Review", className: "bg-purple-500 text-white" },
            completed: { label: "Completed", className: "bg-green-700 text-white" },
          };
          const config = statusConfig[normalizedStatus] || statusConfig.not_started;
          return (
            <span
              className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full whitespace-nowrap ${config.className}`}
            >
              {config.label}
            </span>
          );
        },
      },
      {
        id: "submission",
        header: "Submission",
        cell: ({ row }) => {
          const ua = row.original;
          return ua.submission_id ? (
            <button
              type="button"
              onClick={async () => {
                const url = await api.getSubmissionFileUrl(ua.submission_id!);
                window.open(url, "_blank");
              }}
              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
            >
              <FileText className="w-3.5 h-3.5" />
              PDF
              <ExternalLink className="w-3 h-3" />
            </button>
          ) : (
            <span className="text-gray-400">-</span>
          );
        },
      },
      {
        id: "review",
        header: "Review",
        cell: ({ row }) => {
          const ua = row.original;
          return ua.review_id ? (
            <Link
              to={`/admin/reviews/${ua.review_id}`}
              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
            >
              View
              <ExternalLink className="w-3 h-3" />
            </Link>
          ) : (
            <span className="text-gray-400">-</span>
          );
        },
      },
      {
        id: "score",
        header: "Score",
        cell: ({ row }) => {
          const ua = row.original;
          return ua.final_score !== null && ua.final_score !== undefined ? (
            <span className="text-gray-900 font-medium">{ua.final_score}</span>
          ) : (
            <span className="text-gray-400">-</span>
          );
        },
      },
      {
        id: "action",
        header: "",
        cell: ({ row }) => {
          const ua = row.original;
          // Manual topic assignment is available when the participant hasn't
          // submitted and either the window is still open (locked/active) or
          // they have no topic yet. KC-284: the "still open" clause is what now
          // allows Change Topic during an ACTIVE window — this was previously
          // gated on `time_status === "locked"`, which blocked every active
          // cohort. Closed windows keep their prior behaviour: assignable only
          // while still unassigned.
          const canAssignTopic =
            (ua.time_status !== "closed" || !ua.topic_id) && !ua.submission_id && !controlsLocked;
          const hasRowActions = canAssignTopic || Boolean(ua.submission_id);
          return hasRowActions ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Row actions"
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <MoreVertical className="w-4 h-4 text-gray-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canAssignTopic && (
                  <DropdownMenuItem onClick={() => setAssigningUser(ua)}>
                    {ua.topic_id ? "Change Topic" : "Assign Topic"}
                  </DropdownMenuItem>
                )}
                {ua.submission_id && (
                  <DropdownMenuItem
                    onClick={() => handleDeleteSubmission(ua.submission_id!)}
                    className="text-red-600 focus:text-red-600"
                  >
                    Remove Submission
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              type="button"
              aria-label="Row actions"
              className="p-2 rounded-lg cursor-default"
              disabled
            >
              <MoreVertical className="w-4 h-4 text-gray-300" />
            </button>
          );
        },
      },
    ],
    // `handleDeleteSubmission` is memoised on the stable `deleteSubmission`
    // mutate fn; `setAssigningUser` is a stable state setter. So the memo only
    // recomputes when a genuinely new handler appears or the lock state flips.
    [handleDeleteSubmission, controlsLocked]
  );

  if (isLoadingProgrammes || topicsQuery.isPending) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  const filters = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {effectiveProgramme && (
          <Autocomplete
            options={userOptions}
            value={userFilter}
            onChange={setUserFilter}
            placeholder="Filter by user…"
            emptyMessage="No participants"
            className="w-[200px]"
          />
        )}
      </div>

      {autoAssignWindow && !controlsLocked && (
        <button
          type="button"
          onClick={() => setAutoAssignTarget(autoAssignWindow)}
          className="flex flex-shrink-0 items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <Sparkles className="h-4 w-4" />
          Auto-Assign
        </button>
      )}
    </div>
  );

  return (
    <AdminLayout>
      <div className="flex h-full flex-col">
        {cohortLocked && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
            <Lock className="h-4 w-4 flex-shrink-0" />
            <span>
              This cohort is locked — topic assignments are frozen
              {isSuperadmin ? " for admins. As superadmin you can still make changes." : "."}
            </span>
          </div>
        )}
        <DataTable
          className="h-full"
          columns={columns}
          data={visibleRows}
          isLoading={mappingsQuery.isPending && Boolean(effectiveProgramme)}
          error={mappingsError}
          onRetry={() => mappingsQuery.refetch()}
          emptyMessage={
            effectiveProgramme
              ? "No assignments found for this selection"
              : "Select a programme to view assignments"
          }
          getRowId={(r) => `${r.assignment_id}:${r.user_programme_id}`}
          toolbar={filters}
        />
      </div>

      {/* Topic Assign Modal */}
      {assigningUser && (
        <TopicAssignModal
          open={!!assigningUser}
          userAssignment={assigningUser}
          topics={topics}
          onClose={() => setAssigningUser(null)}
        />
      )}

      {/* Auto-Assign Modal */}
      {autoAssignTarget && (
        <AutoAssignModal
          open={!!autoAssignTarget}
          onClose={() => setAutoAssignTarget(null)}
          assignmentId={autoAssignTarget.assignment.id}
          weekNumber={autoAssignTarget.assignment.week_number}
          assignmentNumber={autoAssignTarget.assignment.assignment_number}
          onSuccess={() => setAutoAssignTarget(null)}
        />
      )}
    </AdminLayout>
  );
}
